import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import ConditionPage from "@/pages/ConditionPage";
import BlogPage from "@/pages/BlogPage";
import AdminPage from "@/pages/AdminPage";
import PillarPage from "@/pages/PillarPage";
import CityConditionPage from "@/pages/CityConditionPage";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import ChatWidget from "@/components/ChatWidget";
import AccessibilityWidget from "@/components/AccessibilityWidget";
import { setCanonical } from "@/utils/seo";

function App() {
  return (
    <div className="App noise-bg min-h-screen">
      <BrowserRouter>
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg">Skip to content</a>
        <AccessibilityWidget />
        <div id="main-wrapper">
        <Navigation />
        <main id="main-content" className="min-h-screen">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/therapy/:slug" element={<ConditionPage />} />
            <Route path="/guide/:slug" element={<PillarPage />} />
            <Route path="/therapy/:conditionSlug/in/:citySlug" element={<CityConditionPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/privacy" element={<LegalPage type="privacy" />} />
            <Route path="/terms" element={<LegalPage type="terms" />} />
            <Route path="/disclaimer" element={<LegalPage type="disclaimer" />} />
            <Route path="/accessibility" element={<LegalPage type="accessibility" />} />
          </Routes>
        </main>
        <Footer />
        </div>
        <CookieConsent />
        <ChatWidget />
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

function LegalPage({ type }) {
  useEffect(() => { setCanonical(`/${type}`); window.scrollTo(0, 0); }, [type]);
  const content = {
    privacy: {
      title: "Privacy Policy",
      body: `Your Privacy is Important to Us

Information We Collect

This sound therapy platform operates entirely in your browser and uses local storage to enhance your experience. We collect and store the following information locally on your device:

- Your therapy session history and statistics
- Personal preferences (volume, duration, favorite conditions)
- Usage analytics (session counts, total minutes, streaks)
- Cookie consent preferences

How We Use Your Information

All information is stored locally in your browser's storage and is used to:

- Personalize your therapy experience
- Track your progress and maintain session streaks
- Remember your preferences between visits
- Provide relevant content and recommendations

Data Storage and Security

Your data is stored exclusively in your browser's local storage. This means:

- No personal information is transmitted to external servers
- Your data remains on your device and under your control
- You can clear this data at any time through your browser settings
- No account creation or personal identification is required

AI Chatbot

Our website features an AI-powered wellness guide ("Sarah") that can answer general questions about sound therapy. Conversations with the chatbot are processed by third-party AI services (OpenAI) to generate responses. Chat messages are not stored permanently or linked to any personal identity. The chatbot is an AI assistant and does not provide medical advice.

Third-Party Services and Advertising

This platform uses the following third-party services:

- Google AdSense: May use cookies to serve relevant advertisements based on your browsing activity. Google's use of advertising cookies enables it and its partners to serve ads based on your visit to this site and other sites on the Internet. You may opt out of personalized advertising by visiting www.aboutads.info.
- Affiliate Links: We may earn a commission from purchases made through affiliate links at no additional cost to you. External sites have their own privacy policies.
- We are not responsible for third-party privacy practices.

Cookies and Local Storage

We use browser local storage (not traditional cookies) to store your preferences and session data. Google AdSense and other third-party services may use cookies. This storage:

- Persists until you clear your browser data
- Is not shared with other websites or services by us
- Can be disabled through your browser settings

California Consumer Privacy Act (CCPA)

If you are a California resident, you have the right to:

- Know what personal data is being collected about you
- Know whether your personal data is sold or disclosed and to whom
- Say no to the sale of personal data
- Access your personal data
- Request deletion of your personal data
- Not be discriminated against for exercising your privacy rights

Since we do not collect personal information beyond browser local storage, most of these rights are automatically satisfied. For any privacy-related requests, contact us at support@lifeownit.com.

General Data Protection Regulation (GDPR)

For users in the European Economic Area, we comply with GDPR requirements. We do not collect, process, or store personal data on our servers. All user data is stored locally in your browser. You have the right to access, rectify, erase, restrict processing, and port your data. Since data is stored locally, you have full control through your browser settings.

Your Rights

You have the right to:

- Clear all stored data through your browser settings
- Disable local storage functionality
- Use the platform without storing any data
- Opt out of personalized advertising
- Contact us with privacy-related questions

Changes to This Policy

We may update this privacy policy from time to time. Changes will be reflected in this document with an updated revision date. Continued use of the platform constitutes acceptance of any changes.

Last Updated: March 2026

Contact: If you have questions about this privacy policy, email us at support@lifeownit.com.`
    },
    terms: {
      title: "Terms of Service",
      body: `Terms of Use for ToneHealing.com

1. Acceptance of Terms

By accessing and using ToneHealing.com ("the Platform"), you accept and agree to be bound by these terms. If you do not agree to these terms, please do not use this service.

2. Description of Service

Our platform provides:

- Binaural beats therapy using Web Audio API technology
- Solfeggio frequency generation for wellness purposes
- Ambient soundscapes for relaxation
- Educational content about sound therapy
- An AI-powered wellness guide chatbot ("Sarah")
- Progress tracking and personalization features
- Blog content about sound therapy topics

3. Medical Disclaimer and Limitations

IMPORTANT: This platform is for wellness and relaxation purposes only.

- This service is NOT intended to diagnose, treat, cure, or prevent any medical condition
- This is NOT a substitute for professional medical advice, diagnosis, or treatment
- Always consult qualified healthcare professionals for serious health concerns
- Individual results may vary and are not guaranteed
- The AI chatbot ("Sarah") is an artificial intelligence assistant, NOT a medical professional. Any information provided by the chatbot is for general educational purposes only and should not be construed as medical advice
- Sound therapy research is ongoing; we make no claims about specific medical outcomes

4. Safety Requirements and Restrictions

You agree to the following safety restrictions:

- DO NOT use while driving, operating machinery, or during activities requiring alertness or attention
- Use only in safe, stationary environments where you can relax without risk
- Discontinue use immediately if experiencing dizziness, nausea, discomfort, or any adverse effects
- Not recommended for individuals with epilepsy, seizure disorders, or photosensitive conditions
- Not recommended for those with hearing impairments without prior medical consultation
- Some frequencies may induce drowsiness, altered states of consciousness, or affect cognitive function
- Keep volume at comfortable levels; excessive volume may damage hearing
- Children should use this platform only under adult supervision

5. User Responsibilities

As a user, you agree to:

- Use the platform responsibly and entirely at your own risk
- Follow all safety guidelines and warnings provided
- Not use the service for any illegal or unauthorized purpose
- Take full responsibility for your use of the platform and any consequences thereof
- Seek professional medical advice when appropriate
- Not rely on this platform as a substitute for medical treatment

6. Affiliate Marketing and Advertising Disclosure

This platform contains affiliate links and advertisements, including Google AdSense. By using this service, you acknowledge that:

- We may earn commissions from affiliate purchases made through links on this site
- Google AdSense displays third-party advertisements which may use cookies
- Product recommendations may be influenced by affiliate relationships
- We are not responsible for third-party products, services, or advertisements
- All affiliate relationships are disclosed in accordance with FTC guidelines (16 CFR Part 255)
- You pay the same price whether you use our affiliate link or purchase directly

7. Intellectual Property

All content on this platform, including but not limited to text, graphics, logos, audio frequencies, and software, is the property of ToneHealing.com or its content suppliers and is protected by copyright laws. You may not reproduce, distribute, or create derivative works without express written permission.

8. Limitation of Liability

TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW:

- We provide this service "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied
- We are not liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from your use of the platform
- You assume all risks associated with using this platform, including any risk to your health or safety
- Our total liability shall not exceed the amount you paid for the service (which is zero, as the platform is free)
- We do not warrant that the service will be uninterrupted, error-free, or free of viruses or other harmful components

9. Indemnification

You agree to indemnify, defend, and hold harmless ToneHealing.com, its operators, affiliates, and partners from any claims, damages, losses, or expenses arising from your use of the platform or violation of these terms.

10. Privacy and Data

Your privacy is important to us. Please review our Privacy Policy to understand how we handle your information. By using this service, you consent to our privacy practices.

11. Modifications to Terms

We reserve the right to modify these terms at any time without prior notice. Changes will be effective immediately upon posting to this page. Your continued use of the platform after changes constitutes acceptance of the modified terms. It is your responsibility to review these terms periodically.

12. Termination

We may terminate or suspend your access to the platform at any time, without prior notice, for any reason whatsoever, including without limitation if you breach these Terms.

13. Severability

If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.

14. Governing Law

These terms shall be governed by and construed in accordance with the laws of the United States. Any disputes arising under these terms shall be resolved in the courts of competent jurisdiction.

Last Updated: March 2026

Contact: Questions about these terms should be directed to support@lifeownit.com.`
    },
    disclaimer: {
      title: "Medical & Legal Disclaimer",
      body: `IMPORTANT NOTICE: Please Read Carefully

This advanced sound healing platform (ToneHealing.com) combines binaural beats, Solfeggio frequencies, and ambient soundscapes for wellness and relaxation purposes only.

Medical Disclaimer

- This platform is NOT intended to diagnose, treat, cure, or prevent any medical condition
- This is NOT a substitute for professional medical advice, diagnosis, or treatment
- Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition
- Never disregard professional medical advice or delay in seeking it because of something you have read or experienced on this platform
- Individual results may vary significantly and are not guaranteed
- The information provided is based on general wellness principles and should not be considered medical guidance

AI Assistant Disclaimer

The AI wellness guide ("Sarah") featured on this platform is an artificial intelligence chatbot. It is NOT a licensed medical professional, therapist, or healthcare provider. Information provided by the AI is for general educational and informational purposes only. Do not rely on AI responses for medical decisions.

Safety Warnings

- DO NOT use sound therapy while driving, operating machinery, or during any activity requiring alertness and attention
- These frequencies are designed to alter brainwave patterns and may cause drowsiness, relaxation, or altered states of consciousness
- Use only in a safe, stationary environment where you can relax without risk
- Some frequencies may induce dizziness, nausea, or discomfort in certain individuals
- Discontinue use immediately if you experience any adverse effects

Contraindications

This platform is NOT recommended for:

- Individuals with epilepsy or seizure disorders
- Individuals with hearing impairments (without medical consultation)
- Individuals with pacemakers or other implanted medical devices (without medical consultation)
- Pregnant women (without medical consultation)
- Children under 12 without adult supervision
- Anyone currently experiencing a medical emergency

Assumption of Risk

By using this platform, you acknowledge and agree that:

- You use this service entirely at your own risk
- You assume full responsibility for safe usage
- You have read and understood all safety warnings
- You will not hold ToneHealing.com liable for any adverse effects
- You will consult healthcare professionals for any medical concerns

Affiliate and Advertising Disclosure

This site contains affiliate links and Google AdSense advertisements. We may earn commissions from qualifying purchases made through affiliate links at no additional cost to you. This helps support our free platform. All affiliate relationships are disclosed in accordance with FTC guidelines.

Contact

For questions, concerns, or to report adverse effects, please contact: support@lifeownit.com

Last Updated: March 2026`
    },
    accessibility: {
      title: "Accessibility Statement",
      body: `Accessibility Commitment

ToneHealing.com is committed to ensuring digital accessibility for all users, including people with disabilities. We continually work to improve the user experience for everyone and apply relevant accessibility standards.

Accessibility Features

Our website includes the following accessibility features:

- Semantic HTML structure with proper heading hierarchy (H1, H2, H3)
- Descriptive alt text on all meaningful images
- Sufficient color contrast between text and background elements
- Keyboard-navigable interactive elements including buttons, links, and form controls
- Mobile-responsive design that works across all screen sizes and devices
- Clear, readable typography with appropriate text sizing
- Focus indicators on interactive elements for keyboard users
- ARIA attributes on dynamic content where appropriate
- Logical content structure and reading order

Standards We Follow

We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. These guidelines explain how to make web content more accessible for people with disabilities and more user-friendly for everyone.

Compatibility

Our website is designed to be compatible with the following assistive technologies:

- Screen readers (such as NVDA, JAWS, and VoiceOver)
- Screen magnification software
- Speech recognition software
- Operating system accessibility features

Known Limitations

While we strive for full accessibility, some areas may have limitations:

- The sound player requires audio capability and headphones for binaural beats functionality
- Some dynamically generated content may not be immediately available to screen readers
- Third-party content (such as Google AdSense advertisements) may not meet all accessibility standards

We are actively working to address any known limitations.

Feedback and Contact

We welcome your feedback on the accessibility of ToneHealing.com. If you encounter any accessibility barriers or have suggestions for improvement, please contact us:

- Email: support@lifeownit.com
- Subject line: Accessibility Feedback

We aim to respond to accessibility feedback within 5 business days and will work to resolve any issues promptly.

Continuous Improvement

We are committed to ongoing accessibility improvements. We regularly review our website to identify and fix accessibility issues, and we incorporate accessibility considerations into all new features and content.

European Accessibility Act (EAA) and ADA Compliance

ToneHealing.com recognizes the importance of the European Accessibility Act (EAA) and the Americans with Disabilities Act (ADA). While our primary audience is in the United States, we are committed to providing an accessible experience for all users regardless of location. We continuously work toward meeting international accessibility standards.

Last Updated: March 2026`
    }
  };

  const page = content[type] || content.disclaimer;

  return (
    <div className="max-w-7xl mx-auto px-4 py-24">
      {/* Hero with meditation globe */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start mb-12">
        <div className="lg:col-span-2">
          <h1 className="text-4xl font-bold mb-8" data-testid={`${type}-title`}>{page.title}</h1>
        </div>
        <div className="hidden lg:flex items-center justify-center relative">
          <div className="absolute w-32 h-32 rounded-full border-2 border-primary/20" style={{ animation: 'ring-pulse-1 3s ease-in-out infinite' }} />
          <div className="absolute w-44 h-44 rounded-full border border-primary/10" style={{ animation: 'ring-pulse-2 4s ease-in-out infinite 0.5s' }} />
          <img src="https://static.prod-images.emergentagent.com/jobs/e586dc18-7e49-4762-9a32-fdcca74463b6/images/4934b7f9e8db17a37a56475081896b2c65939f6c0ba1eb7b6972c30393fe8a80.png" alt="Sound therapy" className="relative z-10 w-28 h-28 object-cover rounded-full drop-shadow-xl animate-wave-float" />
        </div>
      </div>
      <div className="max-w-3xl">
        <div className="prose max-w-none">
        {page.body.split('\n').map((p, i) => {
          if (p.match(/^\d+\.\s/)) return <h3 key={i} className="text-lg font-bold mt-8 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{p}</h3>;
          if (p.match(/^[A-Z][A-Za-z &]+$/) && p.length < 60) return <h2 key={i} className="text-xl font-bold mt-8 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{p}</h2>;
          if (p.startsWith('- ')) return <li key={i} className="text-muted-foreground ml-6 mb-1.5 list-disc text-sm leading-relaxed">{p.replace('- ', '')}</li>;
          if (p.startsWith('IMPORTANT') || p.startsWith('TO THE FULLEST')) return <p key={i} className="text-sm font-semibold mb-4 leading-relaxed">{p}</p>;
          if (p.trim() === '') return <br key={i} />;
          return <p key={i} className="text-muted-foreground mb-3 text-sm leading-relaxed">{p}</p>;
        })}
        </div>
      </div>
    </div>
  );
}

export default App;
